import { PageController } from '@client/components/_core/layout/PageController';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { match } from 'ts-pattern';
import { type Category, CategoryForm } from './CategoryForm';

export const Route = createFileRoute('/console/$orgId/configuration/compliance/mspr/definitions/categories/edit/$id')({
    component: EditCategory,
    params: {
        parse({ id }) {
            return { id: BigInt(id) };
        },
    },
    staticData: {
        presentation: 'overlay',
    },
});

function EditCategory() {
    const { id: categoryId } = Route.useParams();
    const { trpc, trpcUtils } = useTrpc();
    const navigate = useNavigate();
    const orgId = useOrgId();
    const [open, setOpen] = useState(true);
    const { data: category, isPending: isLoading } = trpc.mspr.category.getCategory.useQuery(
        { id: categoryId },
        { refetchOnWindowFocus: false },
    );
    const { mutate: updateCategory, isPending: isUpdating } = trpc.mspr.category.updateCategory.useMutation();

    const handleSubmit = (values: Category) => {
        if (!categoryId) return;
        updateCategory(
            { id: categoryId, name: values.name ?? '' },
            {
                onSuccess: () => {
                    message.success('Category updated');
                    trpcUtils.mspr.category.listCategories.invalidate();
                    setOpen(false);
                },
                onError(error) {
                    errorMessage.show(error);
                },
            },
        );
    };

    return (
        <PageController route={Route}>
            <CategoryForm
                open={open}
                mode="edit"
                data={category}
                state={match({ isLoading, isUpdating })
                    .with({ isUpdating: true }, () => 'submitting' as const)
                    .with({ isLoading: true }, () => 'loading' as const)
                    .otherwise(() => 'ready' as const)}
                onSubmit={handleSubmit}
                onCloseFinished={() => {
                    navigate({ to: '/console/$orgId/configuration/compliance/mspr/definitions', params: { orgId } });
                }}
            />
        </PageController>
    );
}
